import React, { useEffect, useState } from 'react'
import {
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import SearchIcon from '@material-ui/icons/Search'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox'
import { Pagination } from '@material-ui/lab'
import { toast } from 'react-toastify'

import { Container } from '../../../components'
import { axiosInstance, isEmailValid } from '../../../helpers'
import { Company, IdBoolMap, IdentifiableNamedObject } from '../../../models'
import { Selector } from '../../../components/Selector'
import { usePagination } from '../../../hooks'
import { CompanyWithInvoiceCode } from '../Property/types'
import {
    CatalogKey,
    CatalogSelection,
    CopyCatalog,
    CopyItemPicker,
    buildFullSelection,
    getSelectionIds,
} from './CopyItemPicker'

type AnyCompany = Company | CompanyWithInvoiceCode

const getOrganizationId = (company: AnyCompany): number => {
    if ('organization' in company && typeof company.organization === 'number') {
        return company.organization
    }
    return (company as CompanyWithInvoiceCode).organization.id
}

const ROWS_PER_PAGE = 18

export const CopyConfigsPage = () => {
    const theme = useTheme()

    const [allApartments, setAllApartments] = useState<AnyCompany[]>([])
    const [apartmentsLoading, setApartmentsLoading] = useState(false)

    const [organizationList, setOrganizationList] = useState<
        IdentifiableNamedObject[] | null
    >(null)
    const [orgLoading, setOrgLoading] = useState(false)

    const [selectedAptId, setSelectedAptId] = useState(-1)
    const [selectedOrgId, setSelectedOrgId] = useState(-1)
    const [orgApartments, setOrgApartments] = useState<AnyCompany[]>([])
    const [checkedMap, setCheckedMap] = useState<IdBoolMap>({})
    const [searchTerm, setSearchTerm] = useState('')
    const [confirmationEmail, setConfirmationEmail] = useState('')

    const [catalog, setCatalog] = useState<CopyCatalog | null>(null)
    const [catalogLoading, setCatalogLoading] = useState(false)
    const [
        catalogSelection,
        setCatalogSelection,
    ] = useState<CatalogSelection | null>(null)

    useEffect(() => {
        setApartmentsLoading(true)
        axiosInstance
            .get('company/apartment/', {
                params: { all_apartments: true, simple_serializer: true },
            })
            .then((res) => setAllApartments(res.data))
            .catch((e) => toast.error(e))
            .finally(() => setApartmentsLoading(false))

        setOrgLoading(true)
        axiosInstance
            .get('company/organization/')
            .then((res) => setOrganizationList(res.data))
            .catch((e) => toast.error(e))
            .finally(() => setOrgLoading(false))
    }, [])

    useEffect(() => {
        if (selectedAptId === -1) {
            setCatalog(null)
            setCatalogSelection(null)
            return
        }
        setCatalogLoading(true)
        axiosInstance
            .get(`company/apartment/${selectedAptId}/copy_catalog/`)
            .then((res) => {
                const data = res.data as CopyCatalog
                setCatalog(data)
                setCatalogSelection(buildFullSelection(data))
            })
            .catch((e) => toast.error(e))
            .finally(() => setCatalogLoading(false))
    }, [selectedAptId])

    const filteredOrgApartments = orgApartments.filter(
        (apt) =>
            apt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            apt.id.toString().includes(searchTerm),
    )

    const selectedWorkspaceIds = Object.keys(checkedMap).reduce<number[]>(
        (prev, id) => {
            if (id === 'length') return prev
            const numId = Number(id)
            if (checkedMap[numId] === true) return prev.concat(numId)
            return prev
        },
        [],
    )

    const allChecked =
        orgApartments.length > 0 &&
        selectedWorkspaceIds.length === orgApartments.length
    const atLeastOneChecked = selectedWorkspaceIds.length > 0 && !allChecked

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        filteredOrgApartments,
        ROWS_PER_PAGE,
    )

    const handleOrgChange = (newOrgId: number) => {
        setSelectedOrgId(newOrgId)
        const sorted = allApartments
            .filter((apt) => getOrganizationId(apt) === newOrgId)
            .sort((a, b) => a.name.localeCompare(b.name))
        setOrgApartments(sorted)
        setCheckedMap(
            sorted.reduce(
                (prev, apt) => ({ ...prev, [apt.id]: false }),
                {} as IdBoolMap,
            ),
        )
        setSearchTerm('')
    }

    const handleToggleAll = () => {
        const newVal = !allChecked
        const newMap: IdBoolMap = {}
        Object.keys(checkedMap).forEach((k) => {
            newMap[Number(k)] = newVal
        })
        setCheckedMap(newMap)
    }

    const validate = () => {
        if (selectedAptId === -1) {
            toast.error('Please select a source apartment')
            return false
        }
        if (selectedWorkspaceIds.length === 0) {
            toast.error('Please select at least one target apartment')
            return false
        }
        if (!isEmailValid(confirmationEmail)) {
            toast.error('Please enter a valid confirmation email')
            return false
        }
        return true
    }

    const handleCopyDamageConfigs = () => {
        if (!validate()) return
        const body: Record<string, unknown> = {
            targets: selectedWorkspaceIds,
            email: confirmationEmail,
        }
        if (catalog && catalogSelection) {
            const ids = getSelectionIds(
                catalog,
                catalogSelection,
                'damage_configs',
            )
            if (ids !== undefined) body.damage_config_ids = ids
        }
        axiosInstance
            .post(
                `company/apartment/${selectedAptId}/copy_damage_configs/`,
                body,
            )
            .then(() =>
                toast.success(
                    'Request submitted — you will receive an email when done',
                ),
            )
            .catch((e) => toast.error(e))
    }

    const handleCopyInfrastructureAndConfigs = (
        create_infrastructure: boolean,
        copy_configs: boolean,
    ) => {
        if (!validate()) return
        const body: Record<string, unknown> = {
            targets: selectedWorkspaceIds,
            create_infrastructure,
            copy_configs,
            email: confirmationEmail,
        }
        if (catalog && catalogSelection) {
            const addIds = (key: CatalogKey, bodyKey: string) => {
                const ids = getSelectionIds(catalog, catalogSelection, key)
                if (ids !== undefined) body[bodyKey] = ids
            }
            if (copy_configs) {
                addIds('statuses', 'status_ids')
                addIds('status_groups', 'status_group_ids')
                addIds('inventory_configs', 'inventory_config_ids')
                addIds('area_configs', 'area_config_ids')
                addIds('unit_configs', 'unit_config_ids')
                addIds('inventory_groups', 'inventory_group_ids')
                addIds('inspection_types', 'inspection_type_ids')
            }
            if (create_infrastructure) {
                addIds('area_configs', 'area_config_ids')
                addIds('unit_configs', 'unit_config_ids')
                addIds('units', 'unit_ids')
            }
        }
        axiosInstance
            .post(
                `company/apartment/${selectedAptId}/copy_apt_configs_and_infrastructure/`,
                body,
            )
            .then(() =>
                toast.success(
                    'Request submitted — you will receive an email when done',
                ),
            )
            .catch((e) => toast.error(e))
    }

    const grey100 = theme.palette.grey[100]
    const grey200 = theme.palette.grey[200]
    const grey300 = theme.palette.grey[300]
    const primary = theme.palette.primary.main

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                backgroundColor: grey200,
            }}
            scrollY
        >
            {/* ── Page header ── */}
            <Paper
                elevation={0}
                square
                style={{
                    padding: theme.spacing(3, 4),
                    borderBottom: `1px solid ${grey300}`,
                    backgroundColor: '#fff',
                }}
            >
                <Typography
                    variant="h5"
                    style={{
                        fontWeight: 700,
                        color: theme.palette.darkBlue.main,
                    }}
                >
                    Copy Configs
                </Typography>
                <Typography
                    variant="body2"
                    style={{
                        marginTop: theme.spacing(0.5),
                        color: theme.palette.text.secondary,
                    }}
                >
                    Copy damage configs or infrastructure data from a source
                    apartment to multiple target apartments.
                </Typography>
            </Paper>

            {/* ── Two-column layout ── */}
            <Container
                style={{
                    flex: 1,
                    padding: theme.spacing(3),
                    alignItems: 'flex-start',
                }}
            >
                {/* ── Left sidebar: config panel ── */}
                <Paper
                    variant="outlined"
                    style={{
                        width: 300,
                        flexShrink: 0,
                        marginRight: theme.spacing(3),
                        borderRadius: 10,
                        backgroundColor: '#fff',
                        overflow: 'hidden',
                        position: 'sticky',
                        top: theme.spacing(3),
                    }}
                >
                    {/* Sidebar header */}
                    <Container
                        style={{
                            padding: theme.spacing(1.5, 2),
                            backgroundColor: grey100,
                            borderBottom: `1px solid ${grey300}`,
                            alignItems: 'center',
                        }}
                    >
                        <FileCopyIcon
                            style={{
                                color: theme.palette.text.secondary,
                                fontSize: 18,
                                marginRight: theme.spacing(1),
                            }}
                        />
                        <Typography
                            variant="subtitle2"
                            style={{ fontWeight: 600 }}
                        >
                            Configuration
                        </Typography>
                    </Container>

                    <Container
                        style={{
                            flexDirection: 'column',
                            padding: theme.spacing(2),
                        }}
                    >
                        {/* Source apartment */}
                        <Typography
                            variant="overline"
                            style={{
                                color: theme.palette.text.secondary,
                                fontWeight: 700,
                                letterSpacing: 1,
                                marginBottom: theme.spacing(0.5),
                                display: 'block',
                            }}
                        >
                            Source
                        </Typography>
                        {apartmentsLoading ? (
                            <Container
                                style={{
                                    justifyContent: 'center',
                                    padding: theme.spacing(1),
                                }}
                            >
                                <CircularProgress size={24} />
                            </Container>
                        ) : (
                            <Selector
                                label="Source Apartment"
                                currentValue={selectedAptId}
                                onChange={(
                                    e: React.ChangeEvent<{ value: unknown }>,
                                ) => setSelectedAptId(e.target.value as number)}
                                searchable
                                maxItems={100}
                                data={allApartments}
                                getDisplayString={(apt) => apt.name}
                                customStyle={{ formControl: { width: '100%' } }}
                            />
                        )}

                        <Divider style={{ margin: theme.spacing(2, 0) }} />

                        {/* Target organization */}
                        <Typography
                            variant="overline"
                            style={{
                                color: theme.palette.text.secondary,
                                fontWeight: 700,
                                letterSpacing: 1,
                                marginBottom: theme.spacing(0.5),
                                display: 'block',
                            }}
                        >
                            Targets
                        </Typography>
                        {orgLoading ? (
                            <Container
                                style={{
                                    justifyContent: 'center',
                                    padding: theme.spacing(1),
                                }}
                            >
                                <CircularProgress size={24} />
                            </Container>
                        ) : (
                            <Selector
                                label="Organization"
                                data={organizationList ?? []}
                                currentValue={selectedOrgId}
                                onChange={(
                                    e: React.ChangeEvent<{ value: unknown }>,
                                ) => handleOrgChange(e.target.value as number)}
                                getDisplayString={(org) => org.name}
                                customStyle={{ formControl: { width: '100%' } }}
                                maxItems={100}
                                searchable
                            />
                        )}
                    </Container>
                </Paper>

                {/* ── Right panel: apartment list ── */}
                <Paper
                    variant="outlined"
                    style={{
                        flex: 1,
                        borderRadius: 10,
                        backgroundColor: '#fff',
                        overflow: 'hidden',
                        minWidth: 0,
                    }}
                >
                    {/* List header */}
                    <Container
                        style={{
                            padding: theme.spacing(1.5, 2),
                            backgroundColor: grey100,
                            borderBottom: `1px solid ${grey300}`,
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            style={{ fontWeight: 600 }}
                        >
                            Target Apartments
                        </Typography>
                        {selectedWorkspaceIds.length > 0 && (
                            <Chip
                                label={`${selectedWorkspaceIds.length} selected`}
                                size="small"
                                style={{
                                    marginLeft: theme.spacing(1),
                                    backgroundColor: primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    height: 20,
                                }}
                            />
                        )}
                        <div style={{ flex: 1 }} />
                        {/* Select all */}
                        {orgApartments.length > 0 && (
                            <Tooltip
                                title={
                                    allChecked
                                        ? 'Deselect all'
                                        : atLeastOneChecked
                                        ? 'Deselect all'
                                        : 'Select all'
                                }
                            >
                                <Checkbox
                                    checked={allChecked}
                                    indeterminate={atLeastOneChecked}
                                    onChange={handleToggleAll}
                                    style={{ color: primary, padding: 4 }}
                                    checkedIcon={
                                        <CheckBoxIcon fontSize="small" />
                                    }
                                    indeterminateIcon={
                                        <IndeterminateCheckBoxIcon fontSize="small" />
                                    }
                                    icon={
                                        <CheckBoxOutlineBlankIcon fontSize="small" />
                                    }
                                />
                            </Tooltip>
                        )}
                    </Container>

                    {/* Search */}
                    <Container
                        style={{
                            padding: theme.spacing(1.5, 2),
                            borderBottom: `1px solid ${grey300}`,
                            alignItems: 'center',
                        }}
                    >
                        <SearchIcon
                            style={{
                                color: theme.palette.text.secondary,
                                marginRight: theme.spacing(1),
                                fontSize: 20,
                            }}
                        />
                        <TextField
                            fullWidth
                            placeholder="Search by name or ID..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value)
                                setPage(1)
                            }}
                            InputProps={{ disableUnderline: true }}
                        />
                    </Container>

                    {/* Empty state */}
                    {selectedOrgId === -1 && (
                        <Container
                            style={{
                                padding: theme.spacing(6),
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: theme.palette.text.secondary,
                            }}
                        >
                            <FileCopyIcon
                                style={{
                                    fontSize: 48,
                                    marginBottom: theme.spacing(1),
                                    opacity: 0.3,
                                }}
                            />
                            <Typography variant="body2">
                                Select an organization to see its apartments
                            </Typography>
                        </Container>
                    )}

                    {/* Two-column checkbox grid */}
                    {selectedOrgId !== -1 && (
                        <>
                            <Container
                                style={{
                                    padding: theme.spacing(1, 1),
                                    flexWrap: 'wrap',
                                }}
                            >
                                {/* Left column */}
                                <Container
                                    style={{
                                        flexDirection: 'column',
                                        flex: 1,
                                        minWidth: 200,
                                    }}
                                >
                                    {pageData
                                        .filter((_, idx) => idx % 2 === 0)
                                        .map((apt) => (
                                            <AptCheckboxRow
                                                key={apt.id}
                                                apt={apt}
                                                checkedMap={checkedMap}
                                                setCheckedMap={setCheckedMap}
                                            />
                                        ))}
                                </Container>
                                {/* Right column */}
                                <Container
                                    style={{
                                        flexDirection: 'column',
                                        flex: 1,
                                        minWidth: 200,
                                    }}
                                >
                                    {pageData
                                        .filter((_, idx) => idx % 2 === 1)
                                        .map((apt) => (
                                            <AptCheckboxRow
                                                key={apt.id}
                                                apt={apt}
                                                checkedMap={checkedMap}
                                                setCheckedMap={setCheckedMap}
                                            />
                                        ))}
                                </Container>
                            </Container>

                            {/* Pagination footer */}
                            <Divider />
                            <Container
                                style={{
                                    padding: theme.spacing(1, 2),
                                    alignItems: 'center',
                                    backgroundColor: grey100,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    style={{
                                        color: theme.palette.text.secondary,
                                        minWidth: 100,
                                    }}
                                >
                                    {filteredOrgApartments.length > 0
                                        ? `${start}–${end} of ${filteredOrgApartments.length}`
                                        : 'No results'}
                                </Typography>
                                <div style={{ flex: 1 }} />
                                <Pagination
                                    count={numPages}
                                    page={page}
                                    onChange={(_: unknown, newPage: number) =>
                                        setPage(newPage)
                                    }
                                    variant="outlined"
                                    shape="rounded"
                                    size="small"
                                />
                            </Container>
                        </>
                    )}
                </Paper>
            </Container>

            {/* ── Items to copy ── */}
            {(catalog || catalogLoading) && (
                <CopyItemPicker
                    catalog={catalog}
                    catalogLoading={catalogLoading}
                    selection={catalogSelection!}
                    onSelectionChange={setCatalogSelection}
                />
            )}

            {/* ── Actions section ── */}
            <Paper
                variant="outlined"
                style={{
                    borderRadius: 10,
                    backgroundColor: '#fff',
                    overflow: 'hidden',
                    margin: theme.spacing(0, 3, 3),
                }}
            >
                <Container
                    style={{
                        padding: theme.spacing(1.5, 2),
                        backgroundColor: grey100,
                        borderBottom: `1px solid ${grey300}`,
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        Actions
                    </Typography>
                </Container>

                <Container
                    style={{
                        padding: theme.spacing(2, 2.5),
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: theme.spacing(1),
                    }}
                >
                    {/* Confirmation email */}
                    <Typography
                        variant="body2"
                        style={{
                            color: theme.palette.text.secondary,
                            marginRight: theme.spacing(1),
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Confirmation Email
                    </Typography>
                    <TextField
                        placeholder="you@example.com"
                        value={confirmationEmail}
                        onChange={(e) => setConfirmationEmail(e.target.value)}
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 240 }}
                    />

                    <Tooltip
                        title={
                            selectedWorkspaceIds.length === 0
                                ? 'Select at least one target apartment'
                                : ''
                        }
                    >
                        <span>
                            <Button
                                variant="outlined"
                                color="primary"
                                style={{ borderRadius: 8 }}
                                onClick={() =>
                                    handleCopyInfrastructureAndConfigs(
                                        true,
                                        true,
                                    )
                                }
                            >
                                Copy Infrastructure and Configs
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={
                            selectedWorkspaceIds.length === 0
                                ? 'Select at least one target apartment'
                                : ''
                        }
                    >
                        <span>
                            <Button
                                variant="outlined"
                                color="primary"
                                style={{ borderRadius: 8 }}
                                onClick={() =>
                                    handleCopyInfrastructureAndConfigs(
                                        true,
                                        false,
                                    )
                                }
                            >
                                Copy Infrastructure
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={
                            selectedWorkspaceIds.length === 0
                                ? 'Select at least one target apartment'
                                : ''
                        }
                    >
                        <span>
                            <Button
                                variant="outlined"
                                color="primary"
                                style={{ borderRadius: 8 }}
                                onClick={() =>
                                    handleCopyInfrastructureAndConfigs(
                                        false,
                                        true,
                                    )
                                }
                            >
                                Copy Configs
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={
                            selectedWorkspaceIds.length === 0
                                ? 'Select at least one target apartment'
                                : ''
                        }
                    >
                        <span>
                            <Button
                                variant="contained"
                                color="primary"
                                style={{ borderRadius: 8 }}
                                onClick={handleCopyDamageConfigs}
                            >
                                Copy Damage Configs
                            </Button>
                        </span>
                    </Tooltip>
                </Container>
            </Paper>
        </Container>
    )
}

interface AptCheckboxRowProps {
    apt: AnyCompany
    checkedMap: IdBoolMap
    setCheckedMap: (map: IdBoolMap) => void
}

const AptCheckboxRow = ({
    apt,
    checkedMap,
    setCheckedMap,
}: AptCheckboxRowProps) => {
    const theme = useTheme()
    const checked = checkedMap[apt.id] ?? false

    return (
        <FormControlLabel
            control={
                <Checkbox
                    checked={checked}
                    size="small"
                    style={{ color: theme.palette.primary.main }}
                    onChange={() =>
                        setCheckedMap({ ...checkedMap, [apt.id]: !checked })
                    }
                />
            }
            label={
                <Typography variant="body2" noWrap title={apt.name}>
                    {apt.name}
                </Typography>
            }
            style={{
                marginLeft: 0,
                marginRight: 0,
                width: '100%',
                paddingRight: theme.spacing(1),
            }}
        />
    )
}
